/**
 * Feature-pack gates — "does this tenant's pack set include module X?"
 *
 * Thin React layer over the shared registry (`@shared/domain/feature-packs`);
 * Cloud Functions consume the same registry directly, so client and server
 * always agree on what a pack grants.
 *
 * Loading contract (matches useTradePack, NOT useFeatureFlag): while the
 * tenant doc is loading, `featurePacks` is undefined, which resolves to ALL
 * features — so a fully-entitled tenant never sees nav items pop in after
 * first paint. A restricted tenant may briefly mount a gated route before
 * the doc lands; RequireFeature re-renders to its fallback when it does.
 */
import { useMemo } from 'react';
import {
  resolveFeatures,
  type FeatureKey,
} from '@shared/domain/feature-packs';
import { useTenant } from '@/hooks/useTenant';

/** The set of features the current tenant's packs grant. */
export function useFeatures(): Set<FeatureKey> {
  const { tenant } = useTenant();
  const featurePacks = tenant?.featurePacks;
  return useMemo(() => resolveFeatures(featurePacks), [featurePacks]);
}

/** True when the current tenant's packs grant the feature. */
export function useFeatureGate(feature: FeatureKey): boolean {
  return useFeatures().has(feature);
}
